import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import Icon from '../Icon/Icon';
import { GENRE_ICONS } from '../../constants/genres';
import './CreationCard.scss';
import { useNavigate } from 'react-router-dom';
import { getProfilePublicPath } from '../../../helpers/profilePaths';
import ProfilePicture from '../ProfilePicture/ProfilePicture';
import { useOnScreen } from '../../hooks/useOnScreen';
import MediaPlayer from '../MediaPlayer/MediaPlayer';
import CreationSkeleton from './CreationSkeleton';
import { getPreferredStreamMimeType, getPreferredStreamUrl } from '../../../helpers/mediaPlayback';

const CreationCard = React.memo(({
  thumbnailUrl,
  category,
  title,
  price,
  onClick,
  users,
  tags,
  media: rawMedia,
  upload_thumbnail = false,
  onFileChange,
  onDeleteThumbnail, id, setActiveId, activeId
}) => {

  // Handle media as array or object
  const media = Array.isArray(rawMedia) && rawMedia.length > 0 
    ? rawMedia[0] 
    : (rawMedia && !Array.isArray(rawMedia) ? rawMedia : null);

  const ref = useRef(null);
  const isVisible = useOnScreen(ref, 0.7);
  useEffect(() => {
    if (isVisible && setActiveId) {
      setActiveId(id);
    }
  }, [isVisible, id]);

  const isPaid = useMemo(() => price > 0, [price]);
  const navigate = useNavigate();

  const getCategoryIcon = useMemo(() => {
    const categoryLower = category?.toLowerCase();
    return GENRE_ICONS[categoryLower] || 'creations';
  }, [category]);

  // const getAuthorName = useMemo(() => {
  //   if (!users) return 'N/A';
  //   return `${users.firstName || ''} ${users.lastName || ''}`.trim() || 'N/A';
  // }, [users]);

  const getAuthorUsername = useMemo(() => {
    if (!users) return 'N/A';
    return `${users.userName || ''}`.trim() || 'N/A';
  }, [users]);

  const [playbackTime, setPlaybackTime] = useState(0);
  
  const handleTimeUpdate = useCallback((currentTime) => {
    setPlaybackTime(currentTime);
  }, []);

  const handleClick = useMemo(() => {
    return () => {
      if (onClick) {
        onClick(playbackTime);
      }
    };
  }, [onClick, playbackTime]);
  const [imageDimensions, setImageDimensions] = useState(null);
  const preferredStreamUrl = useMemo(() => getPreferredStreamUrl(media), [media]);
  const preferredStreamMimeType = useMemo(() => getPreferredStreamMimeType(media), [media]);

  const getAspectRatioClass = useMemo(() => {
    if (!imageDimensions) return ''; // Wait for real dimensions

    const ratio = imageDimensions.width / imageDimensions.height;

    if (ratio > 1.5) return 'ratio-16-9';      // Landscape (wider)
    if (ratio < 0.7) return 'ratio-9-16';      // Portrait (taller)
    return 'ratio-1-1';                         // Square (close to 1:1)
  }, [imageDimensions]);

  const handleImageLoad = useCallback((event) => {
    const img = event.target;
    const dimensions = {
      width: img.naturalWidth,
      height: img.naturalHeight
    };
    setImageDimensions(dimensions);
  }, [title]);
  const getPrimaryTag = useMemo(() => {
    if (!tags || !Array.isArray(tags)) return 'N/A';
    const primaryTag = tags.find(tag => tag.is_primary);

    return primaryTag ? primaryTag.name : tags[0]?.name || category || 'N/A';
  }, [tags]);
  // const handleImageError = useCallback(() => {
  //   console.warn('Image failed to load:', thumbnailUrl || media?.media_url);
  // }, [thumbnailUrl, media]);

  return (
    <div className={`creation-card mb-24 ${getAspectRatioClass}`} onClick={handleClick} ref={ref}>
      <div className='d-flex aic mb-12'  >

        <div
          onClick={(e) => {
            e.stopPropagation();
            const p = getProfilePublicPath(users);
            if (p) navigate(p);
          }}
        ><ProfilePicture imgUrl={users?.profilePicture} no_edit={true} showIcon={false} width={32} height={32} /></div>
        <p className='fs-xs fg-crea fw-medium-bold my-0 ml-8 tt-uppercase'
          onClick={(e) => {
            e.stopPropagation();
            const p = getProfilePublicPath(users);
            if (p) navigate(p);
          }}
        >{getAuthorUsername}</p>
      </div>
      {upload_thumbnail ?
        (!thumbnailUrl ? <div className="add-creation__preview-upload d-flex f-column">

          <label className="add-creation__upload-button">
            <input
              type="file"
              onChange={(e) => onFileChange(e, 'thumbnail')}
              accept="image/*"
              hidden
            />
            <div className="add-creation__upload-placeholder">
              <i className="fas fa-plus fs-md fw-medium fg-dark"></i>
              <span className=' fs-sm2 fg-dark fw-bold'>Add a thumbnail</span>
              <span className=' fs-xxs fg-grey7'>PNG, JPG or JPEG</span>

            </div>
          </label>
          <div className='d-flex f-row jcsb aie px-48 mb-8 w-100'>
            <div className='d-flex f-column aic'>
              <div className='rect rec1'></div>
              <p className='fs-tiny fw-bold fg-grey7 mt-8'>1080x1920</p>
            </div>
            <div className='d-flex f-column aic jce'>
              <div className='rect rec2 '></div>
              <p className='fs-tiny fw-bold fg-grey7 mt-8'>1080x1080</p>
            </div>
            <div className='d-flex f-column aic jce '>
              <div className='rect rec3'></div>
              <p className='fs-tiny fw-bold fg-grey7 mt-8'>1920x1080</p>
            </div>
          </div>
        </div> :
          <div className="media" style={{ aspectRatio: '1/1' }}>
            <div className="media-container">
              <img
                src={thumbnailUrl}
                alt={title}
                loading="lazy" // Add lazy loading for images
                onLoad={handleImageLoad}
              />

              <span className="trash-thumbnail" onClick={() => onDeleteThumbnail()}>
                <Icon alias="trash" width={14} height={16} stroke="#C9100E" color="none" />
              </span>
            </div>
          </div>
        )

        :
        <div className="media">
          {(thumbnailUrl || media) ? (

            <div className="media-container">
              {activeId===id && media && (media.media_type === "video" || media.media_type === "audio") && media.signed_url && (
                <div
                  className={`media-player-wrapper ${(activeId === id) ? 'active' : 'inactive'}`}
                  style={{ height: "100%", position: "relative" }}
                >
                  <MediaPlayer
                    mediaUrl={media.signed_url}
                    streamUrl={preferredStreamUrl}
                    streamMimeType={preferredStreamMimeType}
                    fileType={media.media_type}
                    mimeType={media.mime_type}
                    isPreview={ activeId == id}
                    posterImage={thumbnailUrl}
                    controls={false}
                    isNotFree={isPaid}
                    isPhysical={media.media_format === "physical"}
                    onTimeUpdate={handleTimeUpdate}
                  />
                  {/* Tap above the control strip opens creation; bottom band stays clear for Video.js controls */}
                  <div
                    className="creation-card-media-hitbox"
                    aria-hidden="true"
                  />
                </div>
              )}
              
              <img
                className={`thumbnail-image ${(activeId === id) && media && (media.media_type === "video" || media.media_type === "audio") ? 'inactive' : 'active'}`}
                src={thumbnailUrl || media?.media_url}
                alt={title}
                loading="lazy"
                onLoad={handleImageLoad}
              />
              {isPaid && (
                <div className="shopping-bag-overlay">
                  <Icon alias="shopping_bag" width={20} color="#FFFFFF" />
                </div>
              )}
            </div>
          ) : (
            <div className="placeholder">
              <Icon
                alias={getCategoryIcon}
                width={32}
                color="#777777"
              />
            </div>
          )}
        </div>
      }
      <div className="mt-12">
        <div className='d-flex aic'>
          <Icon
            alias={getCategoryIcon}
            className="category-icon"
            color="#0A0F1E"
            width={32}
            height={32}
          />
          <div className='ml-8'>
            <p className="my-0 fw-bold">{title}</p>
            <span className="cultural-area">{getPrimaryTag}</span>
          </div>
        </div>
      </div>
    </div>
  );
});

CreationCard.displayName = 'CreationCard';

export default CreationCard; 